// src/components/TaskManagement.tsx
// Refactored with Precision with Warmth design system

import React, { useState, useContext } from 'react';
import { Project, Task, TimeEntry } from '../services/api/types';
import { AuthContext } from '../contexts/AuthAdapter';
import { useMutation } from '../hooks/useApi';
import { apiService } from '../services/api';
import TaskCard from './TaskCard';
import CreateTaskModal from './CreateTaskModal';
import { Button, Modal, Spinner, EmptyState } from '../../shared/components/ui';
import './TaskManagement.css';

interface TaskManagementProps {
  project: Project;
  tasks: Task[];
  selectedDate: string;
  todayEntries: TimeEntry[];
  onTaskUpdated: () => void;
  onTimeLogged?: () => void;
  loading?: boolean;
  availableEmployees?: { id: number; name: string }[];
}

const TaskManagement: React.FC<TaskManagementProps> = ({
  project,
  tasks,
  selectedDate,
  todayEntries,
  onTaskUpdated,
  onTimeLogged,
  loading = false,
  availableEmployees = []
}) => {
  const { user } = useContext(AuthContext);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    taskId: number | null;
    taskName: string;
    type: 'complete' | 'delete' | null;
  }>({ isOpen: false, taskId: null, taskName: '', type: null });

  // Mutations for task operations
  const { mutate: completeTask, loading: completingTask } = useMutation(
    (taskId: number) => apiService.tasks.completeTask(taskId),
    {
      onSuccess: () => {
        onTaskUpdated();
      },
      onError: (error) => {
        console.error('Error completing task:', error);
        const errorMessage = error?.message || 'Unknown error occurred';
        alert(`Failed to complete task: ${errorMessage}`);
      }
    }
  );

  const { mutate: deleteTask, loading: deletingTask } = useMutation(
    (taskId: number) => apiService.tasks.deleteTask(taskId),
    {
      onSuccess: () => {
        onTaskUpdated();
      },
      onError: (error) => {
        console.error('Error deleting task:', error);
        const errorMessage = error?.message || 'Unknown error occurred';
        alert(`Failed to delete task: ${errorMessage}`);
      }
    }
  );

  const { mutate: updateTask, loading: updatingTask } = useMutation(
    ({ taskId, updates }: { taskId: number; updates: { name?: string; description?: string; task_date?: string; expected_hours?: number; completion_percentage?: number } }) =>
      apiService.tasks.updateTask(taskId, updates),
    {
      onSuccess: () => {
        onTaskUpdated();
      },
      onError: (error) => {
        console.error('Error updating task:', error);
        const errorMessage = error?.message || 'Unknown error occurred';
        alert(`Failed to update task: ${errorMessage}`);
      }
    }
  );

  const getLoggedHoursForTask = (taskId: number): number => {
    if (!Array.isArray(todayEntries)) return 0;
    const entry = todayEntries.find(entry => entry.task_id === taskId);
    return entry?.hours || 0;
  };

  const handleTaskComplete = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    setConfirmationModal({
      isOpen: true,
      taskId,
      taskName: task?.name || 'Tarefa desconhecida',
      type: 'complete'
    });
  };

  const handleTaskDelete = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    setConfirmationModal({
      isOpen: true,
      taskId,
      taskName: task?.name || 'Tarefa desconhecida',
      type: 'delete'
    });
  };

  const handleConfirmAction = async () => {
    if (confirmationModal.taskId && confirmationModal.type) {
      if (confirmationModal.type === 'complete') {
        await completeTask(confirmationModal.taskId);
      } else if (confirmationModal.type === 'delete') {
        await deleteTask(confirmationModal.taskId);
      }
    }
    setConfirmationModal({ isOpen: false, taskId: null, taskName: '', type: null });
  };

  const handleCancelAction = () => {
    setConfirmationModal({ isOpen: false, taskId: null, taskName: '', type: null });
  };

  const handleTaskUpdate = async (taskId: number, updates: { name?: string; description?: string; task_date?: string; expected_hours?: number; completion_percentage?: number }) => {
    await updateTask({ taskId, updates });
  };

  const handleTimeLogged = () => {
    onTaskUpdated();
    onTimeLogged?.(); // Call the new handler to refresh summary
  };

  const handleTaskCreated = () => {
    setIsCreateModalOpen(false);
    onTaskUpdated();
  };

  const isBusy = completingTask || deletingTask || updatingTask;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
        <Spinner size="lg" />
        <p className="text-base text-foreground-muted">Carregando suas tarefas...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h3 className="font-display text-lg font-semibold text-foreground">Suas Tarefas</h3>
        <Button
          variant="primary"
          onClick={() => setIsCreateModalOpen(true)}
          disabled={isBusy}
          data-testid="task-create-button"
          leftIcon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          }
        >
          Nova Tarefa
        </Button>
      </div>

      {tasks.length > 0 ? (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(380px,1fr))]">
          {tasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onTaskComplete={handleTaskComplete}
              onTaskDelete={handleTaskDelete}
              onTaskUpdate={handleTaskUpdate}
              loading={{
                completing: completingTask,
                deleting: deletingTask,
                updating: updatingTask,
              }}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-surface-2 py-4">
          <EmptyState
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                <path d="M14 2v6h6M9 15h6M9 11h2" />
              </svg>
            }
            title="Nenhuma tarefa ativa"
            description="Crie uma nova tarefa para começar a trabalhar neste projeto."
            action={
              <Button
                variant="primary"
                onClick={() => setIsCreateModalOpen(true)}
                disabled={isBusy}
                data-testid="task-create-empty-button"
              >
                Criar Primeira Tarefa
              </Button>
            }
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={handleCancelAction}
        size="sm"
        title={confirmationModal.type === 'complete' ? 'Concluir Tarefa?' : 'Excluir Tarefa?'}
        data-testid="task-confirm-modal"
        footer={
          <>
            <Button variant="ghost" onClick={handleCancelAction}>
              Cancelar
            </Button>
            <Button
              variant={confirmationModal.type === 'complete' ? 'success' : 'danger'}
              onClick={handleConfirmAction}
              loading={isBusy}
              data-testid="task-confirm-button"
            >
              {confirmationModal.type === 'complete' ? 'Concluir Tarefa' : 'Excluir Tarefa'}
            </Button>
          </>
        }
      >
        <div className="text-center">
          <p className="font-display text-base font-semibold text-foreground">
            "{confirmationModal.taskName}"
          </p>
          <p className="mt-2 text-sm leading-relaxed text-foreground-muted">
            {confirmationModal.type === 'complete'
              ? 'Esta tarefa será marcada como concluída e movida para o painel do PM. Ela será removida das suas tarefas ativas.'
              : 'Esta ação não pode ser desfeita. A tarefa e todos os seus registros de horas serão excluídos permanentemente.'}
          </p>
        </div>
      </Modal>

      {/* Loading Overlay */}
      {isBusy && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center rounded-xl bg-background/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 text-center">
            <Spinner size="lg" />
            <p className="text-sm text-foreground-muted">
              {completingTask && 'Concluindo tarefa...'}
              {deletingTask && 'Excluindo tarefa...'}
              {updatingTask && 'Atualizando tarefa...'}
            </p>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {isCreateModalOpen && (
        <CreateTaskModal
          projectId={project.id}
          selectedDate={new Date(selectedDate)}
          assignedEmployeeIds={project.assigned_employee_ids}
          onClose={() => setIsCreateModalOpen(false)}
          onTaskCreated={handleTaskCreated}
          availableEmployees={availableEmployees}
        />
      )}
    </div>
  );
};

export default TaskManagement;
